import { useCallback, useMemo, type RefObject } from 'react'
import type { ReactNode } from 'react'
import { TiptapAiEditor } from '@/tiptap/editor/ai-editor'
import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import type { Editor } from '@tiptap/react'
import { Loader2 } from 'lucide-react'
import type { ContentSyncMode } from '@/tiptap/hooks/useAiEditor'

type PolicyContentViewerProps = {
  contentMarkdown?: string | null
  plateContent?: unknown | null
  isLoading?: boolean
  contentKey?: string
  readOnly?: boolean
  reviewMode?: boolean
  enableComments?: boolean
  hideCommentComposer?: boolean
  showSelectionToolbar?: boolean
  docId?: string
  workspaceId?: string
  policySubmissionId?: string
  editorContainerRef?: RefObject<HTMLDivElement | null>
  onCreate?: ({ editor }: { editor: Editor }) => void
  onUpdate?: ({ editor }: { editor: Editor }) => void
  onCommentsUpdated?: (comments: any[] | Map<string, any>) => void
  onCommentActivated?: (commentId: string | null, comment?: any) => void
  onCommentsDataUpdated?: (comments: Map<string, any> | null) => void
  onCommentsError?: (message: string, error: any) => void
  onAddClaimFromSelection?: (payload: {
    docBlockId: string
    quoteText: string
  }) => void
  onAskVera?: (payload: {
    quoteText?: string
    docBlockId?: string
  }) => void
  emptyTitle?: string
  emptyDescription?: string
  emptyState?: ReactNode
  className?: string
  editorClassName?: string
  highlightBlockIds?: string[]
  contentSyncMode?: ContentSyncMode
}

function isRenderablePlateContent(plateContent: unknown): boolean {
  if (!plateContent || typeof plateContent !== 'object') {
    return false
  }

  const content = (plateContent as { content?: unknown }).content
  if (!Array.isArray(content) || content.length === 0) {
    return false
  }

  const isSingleEmptyParagraph =
    content.length === 1 &&
    typeof content[0] === 'object' &&
    content[0] !== null &&
    (content[0] as { type?: string }).type === 'paragraph' &&
    (!Array.isArray((content[0] as { content?: unknown[] }).content) ||
      (content[0] as { content?: unknown[] }).content?.length === 0)

  return !isSingleEmptyParagraph
}

function resolveInitialContent(
  plateContent: unknown,
  contentMarkdown: string | null | undefined
): Record<string, unknown> | undefined {
  if (isRenderablePlateContent(plateContent)) {
    return plateContent as Record<string, unknown>
  }

  if (contentMarkdown && contentMarkdown.trim().length > 10) {
    try {
      const result = deserializeFromMarkdown(contentMarkdown)
      return (result.content as Record<string, unknown>) ?? undefined
    } catch {
      return undefined
    }
  }

  return undefined
}

export function hasRenderablePolicyContent(
  plateContent: unknown,
  contentMarkdown?: string | null
): boolean {
  return (
    isRenderablePlateContent(plateContent) ||
    Boolean(contentMarkdown && contentMarkdown.trim().length > 10)
  )
}

export function PolicyContentViewer({
  contentMarkdown,
  plateContent,
  isLoading = false,
  contentKey,
  readOnly = true,
  reviewMode = false,
  enableComments = false,
  hideCommentComposer = false,
  showSelectionToolbar = true,
  docId,
  workspaceId,
  policySubmissionId,
  editorContainerRef,
  onCreate,
  onUpdate,
  onCommentsUpdated,
  onCommentActivated,
  onCommentsDataUpdated,
  onCommentsError,
  onAddClaimFromSelection,
  onAskVera,
  emptyTitle = 'Ready to generate policy.',
  emptyDescription = 'Generate the policy to create the first draft.',
  emptyState,
  className = 'h-full',
  editorClassName,
  highlightBlockIds,
  contentSyncMode,
}: PolicyContentViewerProps) {
  const initialContent = useMemo(
    () => resolveInitialContent(plateContent, contentMarkdown),
    [contentKey, plateContent, contentMarkdown]
  )
  const hasContent = hasRenderablePolicyContent(plateContent, contentMarkdown)

  const highlightSet = useMemo(
    () => (highlightBlockIds?.length ? new Set(highlightBlockIds) : null),
    [highlightBlockIds]
  )

  const handleCreate = useCallback(
    ({ editor }: { editor: Editor }) => {
      onCreate?.({ editor })

      if (editor.isEmpty) {
        setTimeout(() => {
          if (!editor.isDestroyed && editor.isEmpty) {
            editor.commands.setContent({ type: 'doc', content: [{ type: 'paragraph' }] }, false)
          }
        }, 50)
      }

      if (!highlightSet) return

      // Apply highlight styling to matching block nodes after editor renders
      requestAnimationFrame(() => {
        let firstEl: HTMLElement | null = null

        // Walk TipTap doc nodes to find matching block IDs
        editor.state.doc.descendants((node, pos) => {
          const blockId = node.attrs?.id ?? node.attrs?.['data-dg-id']
          if (!blockId || !highlightSet.has(blockId)) return

          const domNode = editor.view.nodeDOM(pos)
          if (domNode instanceof HTMLElement) {
            domNode.classList.add('evidence-highlight-block')
            if (!firstEl) firstEl = domNode
          }
        })

        // Scroll first highlighted block into view
        if (firstEl) {
          firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    [onCreate, highlightSet]
  )

  return (
    <div ref={editorContainerRef} className={className}>
      {isLoading && (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-emerald-600' />
        </div>
      )}

      {!isLoading && (
        <div className='h-full'>
          <TiptapAiEditor
            key={contentKey ?? 'policy-content'}
            content={initialContent}
            readOnly={readOnly}
            reviewMode={reviewMode}
            enableComments={enableComments}
            hideCommentComposer={hideCommentComposer}
            showSelectionToolbar={showSelectionToolbar}
            docId={docId}
            workspaceId={workspaceId}
            policySubmissionId={policySubmissionId}
            onCreate={handleCreate}
            onUpdate={onUpdate}
            onCommentsUpdated={onCommentsUpdated}
            onCommentActivated={onCommentActivated}
            onCommentsDataUpdated={onCommentsDataUpdated}
            onCommentsError={onCommentsError}
            onAddClaimFromSelection={onAddClaimFromSelection}
            onAskVera={onAskVera}
            editorContentClassName={editorClassName}
            contentSyncMode={contentSyncMode}
          />
        </div>
      )}
    </div>
  )
}
